/**
 * Converts any string into a safe “slug” suitable for use in data-cy attributes.
 * - Lowercases all letters
 * - Replaces any sequence of non-alphanumeric characters with a single hyphen
 * - Trims leading/trailing hyphens
 */
export function slugify(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-') // collapse non-alphanumerics into “-”
    .replace(/(^-|-$)/g, ''); // remove leading/trailing hyphens
}

/**
 * Builds a data-cy value out of a label (and optional index and optional prefix) so that:
 *  • All spaces and punctuation are removed
 *  • Everything is lowercased
 *  • If a prefix is provided, it’s prepended to the slug
 *  • If an index is provided, it’s appended to guarantee uniqueness

 *
 * Example:
 *   makeDataCy("Barrier #1 (High-Risk)", "bowtie")  →  "bowtie-barrier-1-high-risk"
 *   makeDataCy("Central Event")             →  "central-event"
 *   makeDataCy("Central Event", "bowtie", 0) → "bowtie-central-event-0"
 */
export function makeDataCy(label: string, prefix?: string, index?: number): string {
  const base = slugify(label);
  let result = base;

  // If a prefix is provided (and not just whitespace), prepend its slugified value.
  if (prefix?.trim()) {
    result = `${slugify(prefix)}-${result}`;
  }

  // Append the index if it's provided.
  if (typeof index === 'number') {
    result = `${result}-${index}`;
  }

  return result;
}
